// The manual AGGREGATOR — a human-centric surface for pulling a cloud of atoms
// (left) together into events (right), then generating production-ready info for
// each assembled event with AI. This is the human-in-the-loop step between raw
// atoms and Story-Engine production.
//
// Interaction model (one atom → one event):
//   • Drag an atom from the cloud onto an event, or click an atom to drop it into
//     the "active" event. Assigning to a new event removes it from its old one.
//   • Drag an atom (or click its ×) back to the cloud to unassign it.
//   • "Generate production info" enriches an assembled event via AI (headline,
//     synopsis, angle, beats, tags, and the production brief the engine follows).

import { useMemo, useRef, useState } from 'react';
import { Boxes, Check, ChevronDown, Loader2, Plus, RotateCcw, Save, Search, Sparkles, Trash2, Wand2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AtomType, EventBrief, NewsAtom, StoryEvent } from '@/lib/types';
import type { EnrichEventInput, EventEnrichment } from '@/lib/repo';

const DRAG_MIME = 'application/x-lcs-atom';

// Per-type accent so the cloud reads at a glance (left color bar + chip).
const TYPE_META: Record<string, { label: string; bar: string; chip: string }> = {
  event: { label: 'Event', bar: 'border-l-violet-500', chip: 'bg-violet-100 text-violet-700' },
  fact: { label: 'Fact', bar: 'border-l-sky-500', chip: 'bg-sky-100 text-sky-700' },
  quote: { label: 'Quote', bar: 'border-l-amber-500', chip: 'bg-amber-100 text-amber-700' },
  entity: { label: 'Entity', bar: 'border-l-emerald-500', chip: 'bg-emerald-100 text-emerald-700' },
  media: { label: 'Media', bar: 'border-l-pink-500', chip: 'bg-pink-100 text-pink-700' },
  statistic: { label: 'Stat', bar: 'border-l-indigo-500', chip: 'bg-indigo-100 text-indigo-700' },
  context: { label: 'Context', bar: 'border-l-slate-400', chip: 'bg-slate-100 text-slate-600' },
};
const typeMeta = (t: string) =>
  TYPE_META[t] ?? { label: t || 'Atom', bar: 'border-l-slate-300', chip: 'bg-slate-100 text-slate-600' };

// Accent per event card, cycled by position — gives each mold its own identity.
const EVENT_ACCENTS = [
  { dot: 'bg-violet-500', ring: 'ring-violet-400', soft: 'bg-violet-50' },
  { dot: 'bg-sky-500', ring: 'ring-sky-400', soft: 'bg-sky-50' },
  { dot: 'bg-emerald-500', ring: 'ring-emerald-400', soft: 'bg-emerald-50' },
  { dot: 'bg-amber-500', ring: 'ring-amber-400', soft: 'bg-amber-50' },
  { dot: 'bg-pink-500', ring: 'ring-pink-400', soft: 'bg-pink-50' },
  { dot: 'bg-indigo-500', ring: 'ring-indigo-400', soft: 'bg-indigo-50' },
];

const clone = (events: StoryEvent[]): StoryEvent[] => events.map((e) => ({ ...e, atom_ids: [...e.atom_ids] }));

export function EventAggregator({
  topicId,
  atoms,
  events,
  canUseAI,
  onSave,
  onEnrich,
}: {
  topicId: string;
  atoms: NewsAtom[];
  events: StoryEvent[];
  canUseAI: boolean;
  onSave: (draft: StoryEvent[]) => Promise<StoryEvent[] | undefined>;
  onEnrich: (input: EnrichEventInput) => Promise<EventEnrichment | undefined>;
}) {
  const [draft, setDraft] = useState<StoryEvent[]>(() => clone(events));
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [showAssigned, setShowAssigned] = useState(false);
  const [activeEventId, setActiveEventId] = useState<string | null>(events[0]?.event_id ?? null);
  const [dragOverEventId, setDragOverEventId] = useState<string | null>(null);
  const [cloudDragOver, setCloudDragOver] = useState(false);
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const draggingRef = useRef<string | null>(null);

  const atomById = useMemo(() => new Map(atoms.map((a) => [a.atom_id, a])), [atoms]);

  // atom_id → the draft event it currently belongs to (one-atom-one-event).
  const assignment = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of draft) for (const id of e.atom_ids) m.set(id, e.event_id);
    return m;
  }, [draft]);

  const typesPresent = useMemo(() => {
    const s = new Set<string>();
    for (const a of atoms) s.add(a.atom_type);
    return [...s];
  }, [atoms]);

  const cloudAtoms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return atoms.filter((a) => {
      if (!showAssigned && assignment.has(a.atom_id)) return false;
      if (typeFilter.size > 0 && !typeFilter.has(a.atom_type)) return false;
      if (q && !a.statement.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [atoms, assignment, search, typeFilter, showAssigned]);

  const unassignedCount = atoms.length - assignment.size;

  function touch() {
    setDirty(true);
    setJustSaved(false);
    setLocalError(null);
  }

  function assign(atomId: string, toEventId: string) {
    setDraft((prev) =>
      prev.map((e) => ({
        ...e,
        atom_ids:
          e.event_id === toEventId
            ? e.atom_ids.includes(atomId)
              ? e.atom_ids
              : [...e.atom_ids, atomId]
            : e.atom_ids.filter((id) => id !== atomId),
      })),
    );
    touch();
  }

  function unassign(atomId: string) {
    setDraft((prev) => prev.map((e) => ({ ...e, atom_ids: e.atom_ids.filter((id) => id !== atomId) })));
    touch();
  }

  function addEvent() {
    const id = crypto.randomUUID();
    setDraft((prev) => [...prev, { event_id: id, topic_id: topicId, label: '', atom_ids: [] }]);
    setActiveEventId(id);
    touch();
  }

  function removeEvent(eventId: string) {
    setDraft((prev) => prev.filter((e) => e.event_id !== eventId));
    if (activeEventId === eventId) setActiveEventId(null);
    touch();
  }

  function patchEvent(eventId: string, patch: Partial<StoryEvent>) {
    setDraft((prev) => prev.map((e) => (e.event_id === eventId ? { ...e, ...patch } : e)));
    touch();
  }

  function clickAtom(atomId: string) {
    if (assignment.has(atomId)) {
      unassign(atomId);
      return;
    }
    if (!activeEventId) {
      setLocalError('Pick an event first (click its ▸ dot), or create one, then click atoms to add them.');
      return;
    }
    assign(atomId, activeEventId);
  }

  async function reset() {
    setDraft(clone(events));
    setDirty(false);
    setJustSaved(false);
    setLocalError(null);
  }

  async function save() {
    setSaving(true);
    setLocalError(null);
    try {
      const saved = await onSave(draft);
      if (saved) {
        setDraft(clone(saved));
        setDirty(false);
        setJustSaved(true);
        if (activeEventId && !saved.some((e) => e.event_id === activeEventId)) setActiveEventId(saved[0]?.event_id ?? null);
      } else {
        setLocalError('Save failed — check the error above.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function enrich(eventId: string) {
    const ev = draft.find((e) => e.event_id === eventId);
    if (!ev || ev.atom_ids.length === 0) {
      setLocalError('Add atoms to this event before generating production info.');
      return;
    }
    setEnrichingId(eventId);
    setLocalError(null);
    try {
      const res = await onEnrich({ atomIds: ev.atom_ids, label: ev.label || undefined });
      if (res) {
        patchEvent(eventId, {
          label: ev.label.trim() || res.label || ev.label,
          actor: res.frame.actor,
          action: res.frame.action,
          object: res.frame.object,
          location: res.frame.location,
          brief: res.brief,
        });
      } else {
        setLocalError('AI enrichment failed — check the error above.');
      }
    } finally {
      setEnrichingId(null);
    }
  }

  // --- drag plumbing ---
  function onAtomDragStart(e: React.DragEvent, atomId: string) {
    e.dataTransfer.setData(DRAG_MIME, atomId);
    e.dataTransfer.effectAllowed = 'move';
    draggingRef.current = atomId;
  }
  function readDrag(e: React.DragEvent): string | null {
    return e.dataTransfer.getData(DRAG_MIME) || draggingRef.current;
  }

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 shadow-sm">
        <Boxes className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Aggregator</span>
        <Badge variant="secondary">{unassignedCount} unassigned</Badge>
        <Badge variant="info">{draft.length} events</Badge>
        <span className="ml-auto flex items-center gap-2">
          {dirty && (
            <span className="flex items-center gap-1 text-[11px] text-amber-600">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Unsaved
            </span>
          )}
          {justSaved && !dirty && (
            <span className="flex items-center gap-1 text-[11px] text-emerald-600">
              <Check className="h-3.5 w-3.5" />
              Saved
            </span>
          )}
          <Button size="sm" variant="outline" onClick={reset} disabled={saving || !dirty}>
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Discard
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !dirty}>
            {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
            Save events
          </Button>
        </span>
      </div>

      {localError && (
        <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700">{localError}</div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        {/* LEFT — atom cloud */}
        <div
          className={cn(
            'flex max-h-[72vh] flex-col rounded-lg border bg-muted/30 transition',
            cloudDragOver && 'ring-2 ring-slate-400',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setCloudDragOver(true);
          }}
          onDragLeave={() => setCloudDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setCloudDragOver(false);
            const id = readDrag(e);
            if (id) unassign(id);
          }}
        >
          <div className="sticky top-0 z-10 space-y-2 rounded-t-lg border-b bg-card/95 p-3 backdrop-blur">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Atom cloud</span>
              <label className="ml-auto flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-3 w-3"
                  checked={showAssigned}
                  onChange={(e) => setShowAssigned(e.target.checked)}
                />
                Show assigned
              </label>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search atoms…"
                className="w-full rounded-md border border-input bg-background py-1.5 pl-7 pr-2 text-xs
                  focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {typesPresent.length > 1 && (
              <div className="flex flex-wrap gap-1">
                {typesPresent.map((t) => {
                  const on = typeFilter.has(t);
                  const m = typeMeta(t);
                  return (
                    <button
                      key={t}
                      onClick={() =>
                        setTypeFilter((prev) => {
                          const next = new Set(prev);
                          if (next.has(t)) next.delete(t);
                          else next.add(t);
                          return next;
                        })
                      }
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-medium transition',
                        on ? m.chip + ' ring-1 ring-inset ring-current/30' : 'bg-muted text-muted-foreground hover:bg-muted/70',
                      )}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {cloudAtoms.length === 0 ? (
              <div className="py-12 text-center text-xs text-muted-foreground">
                {unassignedCount === 0 && !showAssigned
                  ? 'Every atom is assigned. 🎉 Toggle "Show assigned" to move them.'
                  : 'No atoms match.'}
              </div>
            ) : (
              <div className="space-y-2">
                {cloudAtoms.map((a) => {
                  const m = typeMeta(a.atom_type);
                  const assignedTo = assignment.get(a.atom_id);
                  const corroborated = (a.media_anchor?.length ?? 0) > 1;
                  return (
                    <div
                      key={a.atom_id}
                      draggable
                      onDragStart={(e) => onAtomDragStart(e, a.atom_id)}
                      onDragEnd={() => (draggingRef.current = null)}
                      className={cn(
                        'group relative cursor-grab rounded-md border border-l-4 bg-card p-2 pr-8 text-xs shadow-sm' +
                          ' active:cursor-grabbing hover:border-slate-300',
                        m.bar,
                        assignedTo && 'opacity-60',
                      )}
                    >
                      <div className="mb-1 flex items-center gap-1">
                        <span className={cn('rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase', m.chip)}>{m.label}</span>
                        {corroborated && (
                          <Badge variant="info" className="h-4 px-1 text-[9px]">
                            ×{a.media_anchor.length}
                          </Badge>
                        )}
                        {assignedTo && <span className="ml-auto text-[9px] italic text-muted-foreground">assigned</span>}
                      </div>
                      <p className="line-clamp-3 leading-snug text-foreground/90">{a.statement}</p>
                      <button
                        onClick={() => clickAtom(a.atom_id)}
                        title={assignedTo ? 'Remove from event' : activeEventId ? 'Add to active event' : 'Select an event first'}
                        className="absolute right-1.5 top-1.5 rounded p-1 text-muted-foreground opacity-0 transition
                          group-hover:opacity-100 hover:bg-muted hover:text-foreground"
                      >
                        {assignedTo ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — events */}
        <div className="flex max-h-[72vh] flex-col rounded-lg border bg-card">
          <div className="sticky top-0 z-10 flex items-center gap-2 rounded-t-lg border-b bg-card/95 p-3 backdrop-blur">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Events</span>
            <span className="text-[11px] text-muted-foreground">— drag atoms in, or click an event then click atoms</span>
            <Button size="sm" variant="outline" className="ml-auto" onClick={addEvent}>
              <Plus className="mr-1 h-3.5 w-3.5" /> New event
            </Button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-3">
            {draft.length === 0 ? (
              <div className="rounded-lg border border-dashed py-12 text-center">
                <Boxes className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mx-auto mt-2 max-w-xs text-xs text-muted-foreground">
                  No events yet. Click <span className="font-medium">New event</span>, then drag atoms from the cloud to build it.
                </p>
              </div>
            ) : (
              draft.map((ev, i) => {
                const accent = EVENT_ACCENTS[i % EVENT_ACCENTS.length];
                const active = activeEventId === ev.event_id;
                const over = dragOverEventId === ev.event_id;
                const evAtoms = ev.atom_ids.map((id) => atomById.get(id)).filter(Boolean) as NewsAtom[];
                return (
                  <div
                    key={ev.event_id}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverEventId(ev.event_id);
                    }}
                    onDragLeave={() => setDragOverEventId((c) => (c === ev.event_id ? null : c))}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverEventId(null);
                      const id = readDrag(e);
                      if (id) assign(id, ev.event_id);
                    }}
                    className={cn(
                      'rounded-lg border p-3 transition',
                      over ? `ring-2 ${accent.ring} ${accent.soft}` : active ? 'ring-1 ring-slate-300' : '',
                    )}
                  >
                    {/* header */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setActiveEventId(active ? null : ev.event_id)}
                        title={active ? 'Active target — click to deselect' : 'Make active (click-to-add target)'}
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-full transition',
                          accent.dot,
                          active ? 'ring-2 ring-offset-1 ' + accent.ring : 'opacity-50 hover:opacity-100',
                        )}
                      />
                      <input
                        value={ev.label}
                        onChange={(e) => patchEvent(ev.event_id, { label: e.target.value })}
                        placeholder="Name this event…"
                        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium focus:outline-none focus:ring-0"
                      />
                      <Badge variant="secondary" className="shrink-0">
                        {ev.atom_ids.length}
                      </Badge>
                      <button
                        onClick={() => removeEvent(ev.event_id)}
                        title="Delete event (returns its atoms to the cloud)"
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* frame */}
                    {(ev.actor || ev.action || ev.object || ev.location) && (
                      <p className="mt-1 truncate text-[11px] text-muted-foreground">
                        {[ev.actor, ev.action, ev.object, ev.location].filter(Boolean).join(' · ')}
                      </p>
                    )}

                    {/* drop body */}
                    <div className="mt-2 min-h-[44px] rounded-md border border-dashed p-2">
                      {evAtoms.length === 0 ? (
                        <p className="py-1 text-center text-[11px] text-muted-foreground">
                          Drag atoms here, or select this event and click atoms in the cloud.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {evAtoms.map((a) => {
                            const m = typeMeta(a.atom_type);
                            const key = ev.brief?.key_atom_ids?.includes(a.atom_id);
                            return (
                              <span
                                key={a.atom_id}
                                draggable
                                onDragStart={(e) => onAtomDragStart(e, a.atom_id)}
                                onDragEnd={() => (draggingRef.current = null)}
                                title={a.statement}
                                className={cn(
                                  'inline-flex max-w-[240px] cursor-grab items-center gap-1 rounded-full border' +
                                    ' py-0.5 pl-2 pr-1 text-[11px] active:cursor-grabbing',
                                  m.chip,
                                  key && 'ring-1 ring-current/40',
                                )}
                              >
                                {key && <Sparkles className="h-2.5 w-2.5" />}
                                <span className="truncate">{a.statement}</span>
                                <button onClick={() => unassign(a.atom_id)} className="rounded-full p-0.5 hover:bg-black/10">
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* AI enrichment */}
                    <EventBriefPanel
                      event={ev}
                      canUseAI={canUseAI}
                      enriching={enrichingId === ev.event_id}
                      onEnrich={() => enrich(ev.event_id)}
                      onBriefChange={(text) => patchEvent(ev.event_id, { brief: { ...(ev.brief ?? {}), brief: text } })}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// The AI production-info panel for one event: a "Generate" affordance and, once
// enriched, an expandable view of the brief with an editable production brief.
function EventBriefPanel({
  event,
  canUseAI,
  enriching,
  onEnrich,
  onBriefChange,
}: {
  event: StoryEvent;
  canUseAI: boolean;
  enriching: boolean;
  onEnrich: () => void;
  onBriefChange: (text: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const brief: EventBrief | undefined = event.brief;
  const has = !!brief && (!!brief.headline || !!brief.synopsis || !!brief.brief);
  const empty = event.atom_ids.length === 0;

  return (
    <div className="mt-2 border-t pt-2">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={has ? 'outline' : 'default'}
          className="h-7 text-[11px]"
          onClick={onEnrich}
          disabled={!canUseAI || enriching || empty}
          title={!canUseAI ? 'AI enrichment needs the deployed backend (cloud mode)' : empty ? 'Add atoms first' : undefined}
        >
          {enriching ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />}
          {enriching ? 'Generating…' : has ? 'Regenerate production info' : 'Generate production info'}
        </Button>
        {has && (
          <>
            {brief?.suggested_duration_s ? (
              <Badge variant="secondary" className="text-[10px]">
                ~{brief.suggested_duration_s}s
              </Badge>
            ) : null}
            <button
              onClick={() => setOpen((o) => !o)}
              className="ml-auto flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              {open ? 'Hide' : 'Show'} brief
              <ChevronDown className={cn('h-3.5 w-3.5 transition', open && 'rotate-180')} />
            </button>
          </>
        )}
        {!canUseAI && !has && <span className="text-[10px] italic text-muted-foreground">cloud mode only</span>}
      </div>

      {has && open && brief && (
        <div className="mt-2 space-y-2 rounded-md bg-muted/40 p-2.5 text-xs">
          {brief.headline && <p className="text-sm font-semibold leading-snug">{brief.headline}</p>}
          {brief.synopsis && <p className="text-foreground/80">{brief.synopsis}</p>}
          {brief.angle && (
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground/70">Angle: </span>
              {brief.angle}
            </p>
          )}
          {brief.beats && brief.beats.length > 0 && (
            <div>
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Beats</p>
              <ol className="ml-4 list-decimal space-y-0.5 text-foreground/80">
                {brief.beats.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ol>
            </div>
          )}
          {brief.tags && brief.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {brief.tags.map((t) => (
                <span key={t} className="rounded bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  #{t}
                </span>
              ))}
            </div>
          )}
          <div>
            <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Production brief <span className="font-normal normal-case">— sent to the Story Engine</span>
            </p>
            <textarea
              value={brief.brief ?? ''}
              onChange={(e) => onBriefChange(e.target.value)}
              className="min-h-[70px] w-full rounded border border-input bg-background p-2 text-[11px] leading-snug
                focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Re-exported so callers can reference the atom type set without duplicating it.
export type { AtomType };
